import { message } from "antd";
import { useRef, useState } from "react";
import Upload, { RcFile } from "antd/lib/upload";
import {
  UploadFile,
  UploadFileStatus,
  UploadProps,
} from "antd/lib/upload/interface";
import apiBolsa from "../../services/bolsa-api";
import api from "../../services/api";

export interface IHistory {
  id: string;
  month: number;
  year: number;
  path: string;
}

const isValidFile = (file: UploadFile) => file.type === "application/pdf";
const sameNameFile = (file: UploadFile, files: UploadFile[]) =>
  files.findIndex((item) => item.name === file.name) >= 0;

export const useNotasCorretagem = () => {
  const sameNameFiles = useRef<string[]>([]);
  const [loadingSend, setLoadingSend] = useState(false);
  const [notas, setNotas] = useState<UploadFile<any>[]>([]);
  const [history, setHistory] = useState<IHistory[]>([]);
  const [showNotasCorretagemModal, setShowNotasCorretagemModal] =
    useState(false);

  const beforeUploadNota = (file: RcFile) => {
    if (!isValidFile(file)) {
      message.error("Você só pode fazer upload de arquivos pdf");
      return Upload.LIST_IGNORE;
    }
    if (sameNameFile(file, notas)) {
      sameNameFiles.current.push(file.name);
      message.error("Este arquivo já foi adicionado");
      return Upload.LIST_IGNORE;
    }
  };

  const onChangeNotas = ({
    file,
    fileList,
  }: {
    file: UploadFile;
    fileList: UploadFile[];
  }) => {
    if (file && file.status === "removed") return;
    if (!isValidFile(file)) return;
    if (sameNameFiles.current.includes(file.name)) {
      sameNameFiles.current = sameNameFiles.current.filter(
        (name) => name !== file.name
      );
      return;
    }
    setNotas(
      fileList
        .filter((file) => file.status !== "removed")
        .map((file) => ({ ...file, status: "done" as UploadFileStatus }))
    );
  };

  const onRemoveNota = (file: UploadFile) => {
    setNotas((notas) => notas.filter((nota) => nota.uid !== file.uid));
  };

  const sendNotas = (cpfCnpj: string) => {
    const formData = new FormData();
    notas.forEach((nota) => {
      formData.append("files", nota.originFileObj as File);
    });
    setLoadingSend(true);
    const token = localStorage.getItem("@VeloTax:token");
    apiBolsa
      .post(`/brokerage-notes/readBatch/${cpfCnpj}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        message.success("Informações extraídas com sucesso");
        getHistory(cpfCnpj);
      })
      .catch((err) => {
        const errorMessage =
          err.reponse?.message?.error || "Não foi possivel extrair informações";
        message.error(errorMessage);
        setLoadingSend(false);
      });
  };

  const getHistory = async (
    username: string,
    params?: {
      currentPage?: number;
      filters?: {
        day: number[];
        month: number[];
        year: number[];
      };
    }
  ) => {
    const filterDay = params?.filters?.day || null;
    const filterMonth = params?.filters?.month || null;
    const filterYear = params?.filters?.year || null;
    setLoadingSend(true);
    try {
      const { data } = await api.get(`/clients/getNotasHistory/${username}`, {
        params: {
          day: filterDay,
          month: filterMonth,
          year: filterYear,
        },
      });
      if (data?.message) message.error(data.message);
      else
        setHistory(
          data.map((nota: any) => ({
            ...nota,
            id: `${nota.day}${nota.month}${nota.year}`,
          }))
        );
    } catch (error: any) {
      message.error("Algo errado aconteceu. Tente novamente mais tarde.");
    }
    setLoadingSend(false);
  };

  const downloadNota = async (path: string, month: number, year: number) => {
    try {
      const token = localStorage.getItem("@VeloTax:token");
      const { data } = await apiBolsa.get("/brokerage-notes/download", {
        headers: { Authorization: `Bearer ${token}` },
        params: { path: path.replace("notas/", ""), month, year },
      });
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      message.error("Algo errado aconteceu. Tente novamente mais tarde.");
    }
  };

  const uploadProps: UploadProps = {
    fileList: notas,
    multiple: true,
    listType: "text",
    accept: "application/pdf",
    onRemove: onRemoveNota,
    onChange: onChangeNotas,
    beforeUpload: beforeUploadNota,
    customRequest: () => {},
    showUploadList: {
      showRemoveIcon: true,
      showPreviewIcon: false,
      showDownloadIcon: false,
    },
  };

  return {
    files: notas,
    setFiles: setNotas,
    sendNotas,
    loadingSend,
    uploadProps,
    showNotasCorretagemModal,
    setShowNotasCorretagemModal,
    history,
    getHistory,
    downloadNota,
  };
};
